import { CaretSortIcon } from "@radix-ui/react-icons";
import { BeakerIcon, CogIcon, MessagesSquareIcon, TablePropertiesIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { ChatSettings } from "@/components/Playground/Drawer/ControlBar/ChatSettings";
import { usePlayground } from "@/components/Playground/playground-store";
import { TOUR_TARGET_IDS } from "@/components/Playground/tour/constants";
import { useOptionalPlaygroundTourContext } from "@/components/Playground/tour/context";
import TourDisabledArea from "@/components/Playground/tour/TourDisabledArea";
import TourTargetContainer from "@/components/Playground/tour/TourTargetContainer";
import {
  PLAYGROUND_DRAWER_MODES,
  PLAYGROUND_SESSION_MODES,
  PlaygroundDrawerModeType,
  PlaygroundSessionModeType,
} from "@/components/Playground/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

interface PlaygroundModeSwitcherProps {
  className?: string;
  outputCount: number;
  chatCount: number;
}

export const PlaygroundModeSwitcher = observer(
  ({ className, outputCount, chatCount }: PlaygroundModeSwitcherProps) => {
    const { drawerMode, sessionMode, setPlaygroundView, isChat, mockToolResponses } =
      usePlayground();
    const tour = useOptionalPlaygroundTourContext();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isChatSettingsOpen, setIsChatSettingsOpen] = useState(false);

    const isBatchMode = sessionMode === PLAYGROUND_SESSION_MODES.BATCH;
    const isChatMode = isChat && sessionMode === PLAYGROUND_SESSION_MODES.CHAT;
    const isEvaluateMode = isBatchMode && drawerMode === PLAYGROUND_DRAWER_MODES.EVALUATE;
    const outputLabel = outputCount === 1 ? "Output" : "Outputs";
    let currentLabel = `${outputLabel} (${outputCount})`;
    if (isEvaluateMode) {
      currentLabel = "Evaluate";
    }
    if (isChatMode) {
      currentLabel = `Chat (${chatCount})`;
    }

    const handleSelectSessionMode = (mode: PlaygroundSessionModeType) => {
      setPlaygroundView(mode, PLAYGROUND_DRAWER_MODES.OUTPUT);
      setIsMenuOpen(false);
    };

    const handleSelectDrawerMode = (mode: PlaygroundDrawerModeType) => {
      setPlaygroundView(PLAYGROUND_SESSION_MODES.BATCH, mode);
      setIsMenuOpen(false);
    };

    const handleOpenChatSettings = () => {
      setIsMenuOpen(false);
      window.setTimeout(() => setIsChatSettingsOpen(true), 0);
    };

    const handleMenuOpenChange = (open: boolean) => {
      if (tour?.isActive) {
        if (open) {
          tour.setModeSwitcherOpen(true);
        }
        return;
      }

      setIsMenuOpen(open);
    };

    const menuOpen = tour?.isActive ? tour.isModeSwitcherOpen : isMenuOpen;

    return (
      <>
        <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange}>
          <TourTargetContainer
            id={TOUR_TARGET_IDS.MODE_SWITCHER}
            as="span"
            className="relative isolate inline-flex min-w-0"
          >
            <DropdownMenuTrigger
              hideChevron
              className={cn(
                "inline-flex h-7 w-auto min-w-0 items-center justify-start gap-1 rounded-md border-0 bg-transparent px-1.5 text-sm font-semibold text-gray-900 shadow-none ring-0 transition-colors hover:bg-gray-100 focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:bg-gray-100",
                className,
              )}
              aria-label="Playground mode"
            >
              <span className="truncate">{currentLabel}</span>
              <CaretSortIcon className="h-4 w-4 opacity-50" />
            </DropdownMenuTrigger>
            {tour?.isActive && <TourDisabledArea className="z-30" />}
          </TourTargetContainer>

          <DropdownMenuContent
            align="start"
            className={cn(
              "w-44 rounded-lg border-gray-200 p-1 shadow-lg",
              tour?.isActive && "pointer-events-none",
            )}
          >
            {/* Wrapper div re-enables hover (disabled items drop pointer events) so the tooltip shows. */}
            <BasicTooltip
              content="Turn off mock tool responses to use Output mode"
              shouldShow={mockToolResponses}
            >
              <div>
                <DropdownMenuItem
                  id={TOUR_TARGET_IDS.OUTPUT_WORKFLOW}
                  onSelect={() => handleSelectDrawerMode(PLAYGROUND_DRAWER_MODES.OUTPUT)}
                  disabled={mockToolResponses}
                  className={cn(
                    "w-full rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100",
                    isBatchMode &&
                      drawerMode === PLAYGROUND_DRAWER_MODES.OUTPUT &&
                      "bg-gray-100 text-gray-900",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <TablePropertiesIcon className="h-3.5 w-3.5" />
                    <span>Output</span>
                  </div>
                </DropdownMenuItem>
              </div>
            </BasicTooltip>

            <DropdownMenuItem
              id={TOUR_TARGET_IDS.EVALUATE_WORKFLOW}
              onSelect={() => handleSelectDrawerMode(PLAYGROUND_DRAWER_MODES.EVALUATE)}
              className={cn(
                "w-full rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100",
                isEvaluateMode && "bg-gray-100 text-gray-900",
              )}
            >
              <div className="flex items-center gap-2">
                <BeakerIcon className="h-3.5 w-3.5" />
                <span>Evaluate</span>
              </div>
            </DropdownMenuItem>

            {isChat && (
              <div className="group relative">
                <DropdownMenuItem
                  id={TOUR_TARGET_IDS.CHAT_WORKFLOW}
                  onSelect={() => handleSelectSessionMode(PLAYGROUND_SESSION_MODES.CHAT)}
                  className={cn(
                    "w-full rounded-md px-2 py-1.5 pr-9 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100",
                    isChatMode && "bg-gray-100 text-gray-900",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <MessagesSquareIcon className="h-3.5 w-3.5" />
                    <span>Chat</span>
                  </div>
                </DropdownMenuItem>

                <DropdownMenuItem
                  aria-label="Open chat settings"
                  onSelect={event => {
                    event.preventDefault();
                    handleOpenChatSettings();
                  }}
                  className="absolute right-1 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md p-0 text-gray-400 opacity-70 transition hover:bg-gray-200/70 hover:text-gray-900 hover:opacity-100 focus:bg-gray-200/70 focus:text-gray-900 focus:opacity-100 group-hover:opacity-100"
                >
                  <CogIcon className="h-3.5 w-3.5" />
                </DropdownMenuItem>
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <ChatSettings open={isChatSettingsOpen} onOpenChange={setIsChatSettingsOpen} />
      </>
    );
  },
);
